import { motion } from "framer-motion";
import { Database, Workflow, BarChart2, Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { fadeInUp, staggerContainer } from "@/lib/constants";

const pasos = [
  {
    number: "01",
    icon: <Database className="w-6 h-6" />,
    title: "Conecto e integro tus fuentes de datos",
    description: "ERP, CRM, hojas de Excel, APIs, bases de datos (PostgreSQL, MySQL, SQL Server). Construyo el pipeline ETL que unifica todo en un único modelo coherente, con SQL y Power Query."
  },
  {
    number: "02",
    icon: <Workflow className="w-6 h-6" />,
    title: "Modelo y automatizo el reporting",
    description: "Diseño el modelo de datos con DAX, defino los KPIs reales del negocio (margen, rotación, CAC, días de cobertura) y automatizo la actualización para que los informes se generen solos."
  },
  {
    number: "03",
    icon: <BarChart2 className="w-6 h-6" />,
    title: "Construyo dashboards y análisis avanzados",
    description: "Power BI con foco en decisión, no en decoración. Análisis de márgenes por producto y cliente, detección de fugas, forecasting con Python y alertas automáticas sobre desviaciones."
  }
];

export const ComoTrabajoSection = () => {
  return (
    <section className="relative section-padding overflow-hidden" data-testid="como-trabajo-section">
      <div className="spotlight-2 top-0 left-0" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="text-indigo-400 font-medium text-sm uppercase tracking-wider">
              Pipeline de trabajo
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4">
              De datos crudos a <span className="text-gradient">decisiones operativas</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {pasos.map((paso, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full bg-slate-900 border-slate-800 card-hover relative overflow-hidden">
                  <CardContent className="p-6">
                    <span className="absolute top-4 right-4 text-5xl font-bold text-slate-800/50 font-mono">
                      {paso.number}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4">
                      {paso.icon}
                    </div>
                    <h3 className="font-semibold text-white mb-3 leading-snug">{paso.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{paso.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeInUp} className="max-w-3xl mx-auto">
            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/60 border border-indigo-500/20">
              <Shield className="w-6 h-6 text-indigo-400 flex-shrink-0 mt-0.5" />
              <p className="text-slate-300 text-base leading-relaxed">
                <span className="text-white font-semibold">Perfil técnico, foco operativo.</span>{" "}
                No vendo metodologías ni transformaciones culturales: escribo las consultas SQL,
                construyo el modelo y dejo los dashboards funcionando. Si tu equipo trabaja con
                ERP, Excel o bases de datos, me adapto a tu stack actual.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
